import { useRef, useState } from "react";
import { UploadSimple, DownloadSimple, FileCsv, CheckCircle, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";

const TEMPLATE_CSV = [
  "name,email,phone,sessions_per_week,monthly_rate,currency",
  "Sam Carter,sam@example.com,+447700900111,2,240,gbp",
  "Alex Morgan,alex@example.com,,3,360,gbp",
  "Jamie Fox,jamie@example.com,+447700900333,,,",
].join("\n");

export const MigrationImport = () => {
  const fileRef = useRef(null);
  const [fileName, setFileName] = useState("");
  const [csvText, setCsvText] = useState("");
  const [importing, setImporting] = useState(false);
  const [result, setResult] = useState(null);

  const downloadTemplate = () => {
    const blob = new Blob([TEMPLATE_CSV], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "traina_import_template.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const onPickFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    setResult(null);
    setCsvText(await f.text());
  };

  const runImport = async () => {
    if (!csvText) return;
    setImporting(true);
    try {
      const { data } = await api.post("/migration/import-clients", { csv_text: csvText, send_emails: true });
      setResult(data);
      if (data.imported_count > 0) {
        toast.success(`Imported ${data.imported_count} client${data.imported_count === 1 ? "" : "s"}`);
      } else {
        toast.error("No clients were imported — check the report below");
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally {
      setImporting(false);
    }
  };

  return (
    <section className="card-flat p-6" data-testid="migration-section">
      <div className="flex items-center gap-3 mb-4">
        <FileCsv size={20} weight="duotone" className="text-brand-light" />
        <div>
          <h2 className="font-display text-xl">Migrate from another system</h2>
          <p className="tracker text-zinc-500 text-[10px]">
            Bulk-import clients + session plans from a CSV. Each client gets a welcome email with a set-password link.
          </p>
        </div>
      </div>

      <p className="text-xs text-zinc-500 mb-4">
        Columns: <span className="font-mono text-zinc-300">name, email</span> (required) ·{" "}
        <span className="font-mono text-zinc-300">phone, sessions_per_week, monthly_rate, currency</span> (optional).
        Rows with problems are skipped and reported — the rest import fine.
      </p>

      <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center flex-wrap">
        <button type="button" onClick={downloadTemplate} className="btn-ghost !py-2 !px-4 !text-xs" data-testid="migration-template-btn">
          <DownloadSimple size={14} className="inline mr-1.5 -mt-0.5" />
          Download template
        </button>
        <button type="button" onClick={() => fileRef.current?.click()} className="btn-ghost !py-2 !px-4 !text-xs" data-testid="migration-choose-file-btn">
          <FileCsv size={14} className="inline mr-1.5 -mt-0.5" />
          {fileName || "Choose CSV file"}
        </button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onPickFile} className="hidden" data-testid="migration-file-input" />
        <button
          type="button"
          onClick={runImport}
          disabled={!csvText || importing}
          className="btn-primary !py-2 !px-4 !text-xs disabled:opacity-40"
          data-testid="migration-upload-btn"
        >
          <UploadSimple size={14} className="inline mr-1.5 -mt-0.5" />
          {importing ? "Importing…" : "Import clients"}
        </button>
      </div>

      {result && (
        <div className="mt-5 pt-5 border-t border-white/10 space-y-3" data-testid="migration-result">
          <div className="flex gap-4 flex-wrap">
            <span className="tracker px-2 py-1 border border-emerald-500/30 text-emerald-400 text-[10px]" data-testid="migration-imported-count">
              <CheckCircle size={12} className="inline mr-1 -mt-0.5" />
              {result.imported_count} imported
            </span>
            <span className={`tracker px-2 py-1 border text-[10px] ${result.skipped_count ? "border-amber-500/30 text-amber-400" : "border-zinc-700 text-zinc-500"}`} data-testid="migration-skipped-count">
              <WarningCircle size={12} className="inline mr-1 -mt-0.5" />
              {result.skipped_count} skipped
            </span>
          </div>

          {result.imported.length > 0 && (
            <div className="text-xs text-zinc-400 space-y-1" data-testid="migration-imported-list">
              {result.imported.map((r) => (
                <div key={r.email} className="flex gap-2 flex-wrap items-center">
                  <span className="text-zinc-200">{r.name}</span>
                  <span className="font-mono text-zinc-500">{r.email}</span>
                  {r.plan_attached && <span className="tracker text-[9px] px-1.5 py-0.5 border border-brand-light/40 text-brand-light">PLAN</span>}
                  <span className={`tracker text-[9px] ${r.email_status === "failed" ? "text-red-400" : "text-zinc-500"}`}>
                    email: {r.email_status}
                  </span>
                </div>
              ))}
            </div>
          )}

          {result.skipped.length > 0 && (
            <div className="text-xs space-y-1" data-testid="migration-skipped-list">
              {result.skipped.map((s) => (
                <div key={`${s.row}-${s.email}`} className="text-amber-400/90">
                  Row {s.row}{s.email ? ` (${s.email})` : ""}: {s.reason}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};
